import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Option } from '../../model/professional/option-model';
import { OptionCategory } from '../../model/professional/option-category-model';
import { PackageOpen, Check, AlertCircle, Clock } from 'lucide-react';
import ImagePreview from '../ui/image-preview';

interface OptionsSelectorProps {
  optionCategories: OptionCategory[];
  selectedOptions: Option[];
  onSelectOption: (option: Option, isSelected: boolean) => void;
}

const OptionsSelector: React.FC<OptionsSelectorProps> = ({
  optionCategories,
  selectedOptions,
  onSelectOption
}) => {
  const { t } = useTranslation();

  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewedImage, setPreviewedImage] = useState<{ src: string; alt: string; title: string } | null>(null);

  const isOptionSelected = (optionId: string): boolean => {
    return selectedOptions.some(option => option.id === optionId);
  };

  if (optionCategories.length === 0) {
    return (
      <div className="text-center py-8 px-4 bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col items-center">
          <div className="p-3 bg-gray-100 rounded-full mb-3">
            <PackageOpen className="h-6 w-6 text-gray-400" />
          </div>
          <p className="text-gray-500 font-medium">
            {t('booking.noOptions', 'Aucune option disponible pour ce service')}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {t('booking.continueToNext', 'Vous pouvez continuer à l\'étape suivante')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center mb-6">
          <div className="mr-3 p-2 bg-primary/10 rounded-full">
            <PackageOpen className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">
            {t('booking.options', 'Options disponibles')}
          </h3>
        </div>

        {optionCategories.map(category => (
          <div key={category.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 transition-all duration-300 mb-6 last:mb-0">
            <div className="bg-gradient-to-r from-gray-50 to-white px-5 py-4 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  {category.isRequired && (
                    <div className="mr-2 text-amber-500">
                      <AlertCircle className="h-4 w-4" />
                    </div>
                  )}
                  <h4 className="font-semibold text-gray-800">
                    {category.name}
                    {category.isRequired && (
                      <span className="text-amber-500 ml-1">*</span>
                    )}
                  </h4>
                </div>
                <span className={`text-xs font-medium px-3 py-1.5 rounded-full flex items-center ${category.isMultipleChoice 
                  ? 'bg-secondary/10 text-secondary-700' 
                  : 'bg-primary/10 text-primary-700'}`}>
                  {category.isMultipleChoice 
                    ? t('booking.multipleChoice', 'Choix multiple') 
                    : t('booking.singleChoice', 'Choix unique')}
                </span>
              </div>
              {category.description && (
                <p className="text-sm text-gray-600 mt-2">{category.description}</p>
              )}
            </div>

            <div className="p-5 bg-white">
              <div className="grid gap-3">
                {category.options && category.options.map(option => {
                  const selected = isOptionSelected(option.id);
                  const optionImage = option.imageVariants?.medium || option.image;

                  return (
                    <div key={option.id} className="relative">
                      <label className={`flex items-start cursor-pointer p-3.5 rounded-lg transition-all duration-200 ${selected 
                        ? 'bg-primary/5 border border-primary/20' 
                        : 'hover:bg-gray-50 border border-transparent hover:border-gray-100'}`}>
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`w-5 h-5 rounded-${category.isMultipleChoice ? 'sm' : 'full'} flex items-center justify-center border ${selected 
                            ? 'bg-primary border-primary text-white' 
                            : 'border-gray-300 bg-white'}`}>
                            {selected && <Check className="h-3.5 w-3.5" />}
                          </div>
                          <input
                            type={category.isMultipleChoice ? 'checkbox' : 'radio'}
                            className="sr-only"
                            checked={selected}
                            onChange={(e) => onSelectOption(option, e.target.checked)}
                            name={`category-${category.id}`}
                          />
                        </div>
                        <div className="ml-3 flex-grow">
                          <div className="flex justify-between items-start">
                            <div className="flex items-start">
                              {optionImage && (
                                <div 
                                  className="mr-3 flex-shrink-0 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewedImage({
                                      src: optionImage,
                                      alt: option.name,
                                      title: option.name
                                    });
                                    setShowImagePreview(true);
                                  }}
                                >
                                  <img 
                                    src={optionImage} 
                                    alt={option.name}
                                    className="w-16 h-16 object-cover rounded-md border border-gray-200"
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = 'https://via.placeholder.com/80?text=No+Image';
                                    }}
                                  />
                                </div>
                              )}
                              <div>
                                <span className={`font-medium ${selected ? 'text-primary-800' : 'text-gray-800'}`}>{option.name}</span>
                                {option.description && (
                                  <p className="text-sm text-gray-600 mt-1.5">{option.description}</p>
                                )}
                                {option.durationMinutes && option.durationMinutes > 0 && (
                                  <div className="flex items-center mt-1.5 text-xs text-gray-500">
                                    <span>+{option.durationMinutes} min</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {option.price > 0 && (
                              <span className={`font-semibold ml-2 px-3 py-1.5 rounded-full text-sm ${selected
                                ? 'bg-primary/20 text-primary-700'
                                : 'bg-gray-100 text-gray-700'}`}>
                                +{typeof option.price === 'number' ? option.price.toFixed(2) : parseFloat(String(option.price)).toFixed(2)}€
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {category.isRequired && (
              <div className="px-5 py-3 bg-amber-50 border-t border-amber-100 flex items-center">
                <AlertCircle className="h-4 w-4 text-amber-500 mr-2" />
                <span className="text-xs text-amber-700">
                  {t('booking.requiredCategory', 'Cette option est obligatoire')}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modale ImagePreview */}
      <ImagePreview 
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        imageSrc={previewedImage?.src || ''}
        imageAlt={previewedImage?.alt || ''}
        title={previewedImage?.title || ''}
      />
    </>
  );
};

export default OptionsSelector;